/**
 * ModulesContext
 *
 * Centraliza o estado de filtros e dados de cada módulo (Financeiro, Suprimentos,
 * Comercial, Orçamento, Gerencial) em um contexto que persiste acima do roteador.
 *
 * Isso garante que navegar entre abas do menu não descarte os dados já carregados.
 * Os dados só são buscados novamente quando o usuário clica em "Aplicar" / "Atualizar".
 * No Gerencial, período e centros de custo só disparam o Sienge após "Aplicar" (sem debounce automático).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { FilterValues } from "@/components/ui/FilterBar/types";
import { MultiSelectOption } from "@/components/ui/MultiSelect";
import api from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import { fetchOutcome, fetchIncome, fetchAccountsStatements, fetchBankMovementFinanceiroSummary, includeRecebimentosBankMovementForCostCenterFilter, includePagamentosBankMovementForCostCenterFilter, type FinanceiroData, type OutcomeItem, type IncomeItem, type BankStatementMovement } from "@/services/financeiro";
import { fetchComercialData, type ComercialData } from "@/services/comercial";
import {
  KanbanComprasData,
  PedidoCard,
  OrderItem,
  fetchKanbanCompras,
  fetchItemsByOrderIds,
} from "@/services/suprimentos";
import {
  OrcamentoData,
  fetchBudgetResources,
  fetchAppropriatedResources,
} from "@/services/orcamento";
import {
  GerencialData,
  fetchGerencialData,
} from "@/services/gerencial";
import { buildSupplierOptionsFromKanban } from "@/pages/Suprimentos/kanbanBoardFilter";
import {
  emptyChaoListCache,
  type ChaoDeObraListCache,
} from "@/pages/Gerencial/ChaoDeObra/chaoListState";
import { currentDatePeriod, currentLocalDateInput } from "@/utils/dateInput";

function settledReason(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === "object" && reason !== null && "response" in reason) {
    const err = (reason as { response?: { data?: { error?: string } } }).response?.data?.error;
    if (typeof err === "string" && err.trim()) return err.trim();
  }
  return String(reason);
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function standardDefaultFilters(): FilterValues {
  const { startDate, endDate } = currentDatePeriod();
  return {
    startDate,
    endDate,
    costCenter: [],
    constructiveUnit: [],
    itemFilter: [],
    quotationFilter: "",
    purchaseRequestFilter: "",
    orderFilter: "",
    supplierFilter: [],
  };
}

function orcDefaultDates() {
  return currentDatePeriod();
}

function gerencialDefaultDates(): FilterValues {
  const currentDate = currentLocalDateInput();
  return {
    startDate: currentDate,
    endDate: currentDate,
    costCenter: [],
    constructiveUnit: [],
    itemFilter: [],
    quotationFilter: "",
    purchaseRequestFilter: "",
    orderFilter: "",
    supplierFilter: [],
  };
}

const BATCH_SIZE = 80;
function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/* ─── Cost Centers (compartilhado) ─────────────────────────────────────────── */

interface RawCC { id?: number; name?: string; code?: string }

function extractCCList(data: unknown): RawCC[] {
  if (Array.isArray(data)) return data as RawCC[];
  if (data == null || typeof data !== "object") return [];
  const o = data as Record<string, unknown>;
  /* `content` vazio com `results` preenchido — mesmo padrão que outras APIs Sienge. */
  const keys = ["results", "data", "content", "items", "costCenters", "records", "elements"] as const;
  for (const key of keys) {
    const arr = o[key];
    if (Array.isArray(arr) && arr.length > 0) return arr as RawCC[];
  }
  for (const key of keys) {
    const arr = o[key];
    if (Array.isArray(arr)) return arr as RawCC[];
  }
  return [];
}
function toOption(cc: RawCC): MultiSelectOption {
  const label = cc.code
    ? `${cc.code} – ${cc.name ?? ""}`.trim()
    : (cc.name ?? String(cc.id));
  return { value: String(cc.id ?? ""), label };
}

const CC_SESSION_KEY_PREFIX = "pbisemog_cc_options_v1";
const CC_SESSION_TTL_MS = 15 * 60 * 1000;

function ccSessionKey(userId: string | undefined): string {
  return userId ? `${CC_SESSION_KEY_PREFIX}_${userId}` : CC_SESSION_KEY_PREFIX;
}

function readCostCentersSession(userId: string | undefined): MultiSelectOption[] | null {
  try {
    const raw = sessionStorage.getItem(ccSessionKey(userId));
    if (!raw) return null;
    const o = JSON.parse(raw) as { at?: number; options?: MultiSelectOption[] };
    if (o.at == null || !Array.isArray(o.options) || o.options.length === 0) return null;
    if (Date.now() - o.at > CC_SESSION_TTL_MS) return null;
    return o.options.filter((x) => x && String(x.value).trim() !== "");
  } catch {
    return null;
  }
}

function writeCostCentersSession(userId: string | undefined, options: MultiSelectOption[]): void {
  try {
    sessionStorage.setItem(ccSessionKey(userId), JSON.stringify({ at: Date.now(), options }));
  } catch {
    /* quota / modo privado */
  }
}

/* ─── Tipos exportados ─────────────────────────────────────────────────────── */

export type { FinanceiroData };

export interface StdModuleState<TData> {
  values:   FilterValues;
  applied:  FilterValues;
  setValues: (v: FilterValues) => void;
  apply:    () => void;
  reset:    () => void;
  /** Dispara o primeiro carregamento; chamadas subsequentes são no-op. */
  autoLoad: () => void;
  data:     TData | null;
  loading:  boolean;
  error:    string | null;
}

export interface SuprimentosModuleState extends StdModuleState<KanbanComprasData> {
  orderItems:   OrderItem[];
  loadingItems: boolean;
  errorItems:   string | null;
  /** Fornecedores distintos no Kanban carregado (para o MultiSelect). */
  supplierOptions: MultiSelectOption[];
  /** Itens/insumos distintos dos pedidos carregados (para o MultiSelect). */
  itemOptions: MultiSelectOption[];
  /** Aplica filtros e recarrega o Kanban (evita race com setValues + apply). */
  applyWith: (f: FilterValues) => void;
}

export interface OrcamentoFilters {
  buildingId: string;
  startDate:  string;
  endDate:    string;
}

export interface OrcamentoModuleState {
  buildingId:    string;
  setBuildingId: (id: string) => void;
  startDate:     string;
  setStartDate:  (d: string) => void;
  endDate:       string;
  setEndDate:    (d: string) => void;
  applied:       OrcamentoFilters | null;
  /** Aplica filtros correntes e dispara fetch. */
  apply:         () => void;
  /** Aplica um conjunto de filtros diretamente (usado no auto-select). */
  applyWith:     (f: OrcamentoFilters) => void;
  reset:         () => void;
  /** Ref que controla o auto-select do primeiro CC — persiste entre navegações. */
  didAutoSelect: React.MutableRefObject<boolean>;
  data:          OrcamentoData | null;
  loading:       boolean;
  error:         string | null;
}

/** Lista Chão de Obra — overlay global (Aplicar / Atualizar) e transição para página da unidade (spinner). */
export interface ChaoDeObraModuleState {
  listLoading: boolean;
  setListLoading: (loading: boolean) => void;
  /** True após clicar numa unidade até a página da unidade concluir o primeiro load. */
  unitTransitionLoading: boolean;
  setUnitTransitionLoading: (loading: boolean) => void;
  /** Mantém filtros e dados da lista ao voltar da unidade / relatório. */
  listCache: ChaoDeObraListCache;
  persistListCache: (patch: Partial<ChaoDeObraListCache>) => void;
  clearListCache: () => void;
}

interface ModulesContextValue {
  ccOptions:   MultiSelectOption[];
  loadingCC:   boolean;
  ccNameMap:   Map<string, string>;
  financeiro:  StdModuleState<FinanceiroData>;
  suprimentos: SuprimentosModuleState;
  comercial:   StdModuleState<ComercialData>;
  orcamento:   OrcamentoModuleState;
  gerencial:   StdModuleState<GerencialData>;
  chaoDeObra:  ChaoDeObraModuleState;
}

/* ─── Context ─────────────────────────────────────────────────────────────── */

const ModulesContext = createContext<ModulesContextValue | null>(null);

export function useModules(): ModulesContextValue {
  const ctx = useContext(ModulesContext);
  if (!ctx) throw new Error("useModules deve ser usado dentro de <ModulesProvider>");
  return ctx;
}

/* ─── Provider ────────────────────────────────────────────────────────────── */

export function ModulesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  /* ── Cost Centers ── */
  const [ccOptions, setCcOptions] = useState<MultiSelectOption[]>([]);
  /* Começa em true para que allCcIds inicie como undefined e nenhum módulo
     dispare fetch antes de os centros de custo estarem prontos. */
  const [loadingCC, setLoadingCC] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const userId = user?.id;
    setCcOptions([]);
    const cached = readCostCentersSession(userId);
    if (cached && cached.length > 0) {
      setCcOptions(cached);
      setLoadingCC(false);
    } else {
      setLoadingCC(true);
    }

    api
      .get("/api/cost-centers")
      .then(({ data }) => {
        if (cancelled) return;
        const opts = extractCCList(data).map(toOption);
        setCcOptions(opts);
        if (opts.length > 0) writeCostCentersSession(userId, opts);
      })
      .catch(() => {
        if (cancelled) return;
        /* Mantém sessionStorage se a rede falhar nesta aba. */
        if (!(cached && cached.length > 0)) setCcOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCC(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const ccNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of ccOptions) m.set(o.value, o.label);
    return m;
  }, [ccOptions]);

  const allCcIds = useMemo(
    () => (loadingCC ? undefined : ccOptions.map((o) => o.value)),
    [loadingCC, ccOptions],
  );

  /* ═══════════════════════════════════════════════════════════════════════════
   * FINANCEIRO
   * ════════════════════════════════════════════════════════════════════════ */

  const [finValues,  setFinValues]  = useState<FilterValues>(standardDefaultFilters);
  const [finApplied, setFinApplied] = useState<FilterValues>(standardDefaultFilters);
  const [finData,    setFinData]    = useState<FinanceiroData | null>(null);
  const [finLoading, setFinLoading] = useState(false);
  const [finError,   setFinError]   = useState<string | null>(null);
  const finAbortRef          = useRef<AbortController | null>(null);
  const finHasLoadedRef      = useRef(false);
  const finApplyRef          = useRef<() => void>(() => {});
  const [finRefreshTick, setFinRefreshTick] = useState(0);
  const finAppliedFetchKey = useMemo(() => {
    const cc = Array.isArray(finApplied.costCenter) ? [...finApplied.costCenter].sort().join(",") : "";
    return [finApplied.startDate, finApplied.endDate, cc].join("|");
  }, [finApplied]);

  const finApply = useCallback(() => {
    finHasLoadedRef.current = true;
    setFinApplied((prev) => ({ ...prev, ...finValues }));
    setFinRefreshTick((n) => n + 1);
  }, [finValues]);

  useEffect(() => { finApplyRef.current = finApply; }, [finApply]);

  const finAutoLoad = useCallback(() => {
    if (!finHasLoadedRef.current) finApplyRef.current();
  }, []);

  const finReset = useCallback(() => {
    const defaults = standardDefaultFilters();
    setFinValues(defaults);
    finHasLoadedRef.current = true;
    setFinApplied({ ...defaults });
  }, []);

  useEffect(() => {
    if (!finHasLoadedRef.current) return;
    if (allCcIds === undefined) return;

    finAbortRef.current?.abort();
    const ctrl = new AbortController();
    finAbortRef.current = ctrl;

    setFinLoading(true);
    setFinError(null);
    const filters: FilterValues = {
      ...finApplied,
      costCenter: Array.isArray(finApplied.costCenter) ? finApplied.costCenter : [],
    };

    const includeBmRecebimentos = includeRecebimentosBankMovementForCostCenterFilter(
      filters.costCenter,
      allCcIds,
    );
    const includeBmPagamentos = includePagamentosBankMovementForCostCenterFilter(
      filters.costCenter,
      allCcIds,
    );

    Promise.allSettled([
      fetchOutcome(filters, allCcIds),
      fetchIncome(filters, allCcIds),
      fetchAccountsStatements(filters.startDate, filters.endDate, ctrl.signal),
      fetchBankMovementFinanceiroSummary(filters.startDate, filters.endDate, ctrl.signal),
    ])
      .then((results) => {
        if (!ctrl.signal.aborted) {
          const errs: string[] = [];

          const outcome: OutcomeItem[] =
            results[0].status === "fulfilled" ? results[0].value : ([] as OutcomeItem[]);
          if (results[0].status === "rejected") {
            errs.push(`Contas a pagar: ${settledReason(results[0].reason)}`);
          }

          const income: IncomeItem[] =
            results[1].status === "fulfilled" ? results[1].value : ([] as IncomeItem[]);
          if (results[1].status === "rejected") {
            errs.push(`Recebimentos (income): ${settledReason(results[1].reason)}`);
          }

          const statements: BankStatementMovement[] =
            includeBmRecebimentos && results[2].status === "fulfilled"
              ? results[2].value
              : ([] as BankStatementMovement[]);
          if (includeBmRecebimentos && results[2].status === "rejected") {
            errs.push(`Extrato: ${settledReason(results[2].reason)}`);
          }

          let recebimentosBankMovementTotal: number | undefined;
          let pagamentosBankMovementTotal: number | undefined;
          let bankMovementPagamentoLines = undefined as FinanceiroData["bankMovementPagamentoLines"];
          let bankMovementRecebimentoLines = undefined as FinanceiroData["bankMovementRecebimentoLines"];
          if (results[3].status === "fulfilled") {
            const bv = results[3].value;
            if (bv) {
              if (includeBmPagamentos) {
                pagamentosBankMovementTotal = bv.pagamentosTotal;
                if (bv.pagamentosItems?.length) {
                  bankMovementPagamentoLines = bv.pagamentosItems;
                }
              }
              if (includeBmRecebimentos) {
                recebimentosBankMovementTotal = bv.recebimentosTotal;
                if (bv.recebimentosItems?.length) {
                  bankMovementRecebimentoLines = bv.recebimentosItems;
                }
              }
            }
          } else if (includeBmRecebimentos || includeBmPagamentos) {
            recebimentosBankMovementTotal = undefined;
            pagamentosBankMovementTotal = undefined;
            errs.push(`Total recebimentos (bank-movement): ${settledReason(results[3].reason)}`);
          }

          const payload: FinanceiroData = { outcome, income, statements };
          if (recebimentosBankMovementTotal !== undefined) {
            payload.recebimentosBankMovementTotal = recebimentosBankMovementTotal;
            payload.recebimentosBankMovementOperacaoTotal = recebimentosBankMovementTotal;
          }
          if (pagamentosBankMovementTotal !== undefined) {
            payload.pagamentosBankMovementTotal = pagamentosBankMovementTotal;
          }
          if (bankMovementPagamentoLines) payload.bankMovementPagamentoLines = bankMovementPagamentoLines;
          if (bankMovementRecebimentoLines) payload.bankMovementRecebimentoLines = bankMovementRecebimentoLines;
          setFinData(payload);
          setFinLoading(false);
          setFinError(errs.length > 0 ? errs.join(" · ") : null);
        }
      })
      .catch((e) => {
        if (!ctrl.signal.aborted) {
          setFinError(e instanceof Error ? e.message : String(e));
          setFinLoading(false);
        }
      });

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finAppliedFetchKey, finRefreshTick, allCcIds]);

  /* ═══════════════════════════════════════════════════════════════════════════
   * SUPRIMENTOS
   * ════════════════════════════════════════════════════════════════════════ */

  const [supValues,  setSupValues]  = useState<FilterValues>(standardDefaultFilters);
  const [supApplied, setSupApplied] = useState<FilterValues>(standardDefaultFilters);
  const [supData,    setSupData]    = useState<KanbanComprasData | null>(null);
  const [supLoading, setSupLoading] = useState(false);
  const [supError,   setSupError]   = useState<string | null>(null);
  const supHasLoadedRef = useRef(false);
  const supApplyRef     = useRef<() => void>(() => {});
  const supPendingPayloadRef = useRef<FilterValues | null>(null);
  const [supRefreshTick, setSupRefreshTick] = useState(0);

  /**
   * Suprimentos: a API só é consultada ao clicar «Aplicar / Atualizar» ou no reset (ou primeiro autoLoad).
   * No Kanban, os campos Solicitação / Cotação / Pedido filtram em tempo real no cliente — ver página do Kanban.
   */
  const supApply = useCallback(() => {
    supHasLoadedRef.current = true;
    supPendingPayloadRef.current = null;
    setSupRefreshTick((n) => n + 1);
  }, []);

  const supApplyWith = useCallback((f: FilterValues) => {
    supHasLoadedRef.current = true;
    setSupValues(f);
    supPendingPayloadRef.current = f;
    setSupRefreshTick((n) => n + 1);
  }, []);

  useEffect(() => { supApplyRef.current = supApply; }, [supApply]);

  const supAutoLoad = useCallback(() => {
    if (!supHasLoadedRef.current) supApplyRef.current();
  }, []);

  const supReset = useCallback(() => {
    const defaults = standardDefaultFilters();
    setSupValues(defaults);
    supHasLoadedRef.current = true;
    setSupApplied({ ...defaults });
    setSupRefreshTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!supHasLoadedRef.current) return;

    setSupLoading(true);
    setSupError(null);

    const payload: FilterValues = supPendingPayloadRef.current ?? {
      ...supValues,
      costCenter: Array.isArray(supValues.costCenter) ? supValues.costCenter : [],
    };
    supPendingPayloadRef.current = null;

    fetchKanbanCompras(payload)
      .then((result) => {
        setSupData(result);
        setSupLoading(false);
        setSupApplied(payload);
      })
      .catch((err: unknown) => {
        const msg =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          (err instanceof Error ? err.message : "Erro ao carregar dados de suprimentos.");
        setSupError(msg);
        setSupLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só refetch no tick (Apply / reset / autoLoad)
  }, [supRefreshTick]);

  /* ── Order Items (derivado dos pedidos do kanban) ── */
  const supPedidos = useMemo<PedidoCard[]>(
    () => supData ? [...supData.pedidosSemFinanceiro, ...supData.pedidosGeradoFinanceiro] : [],
    [supData],
  );

  const supSupplierOptions = useMemo(
    () => buildSupplierOptionsFromKanban(supData),
    [supData],
  );

  const [supOrderItems,   setSupOrderItems]   = useState<OrderItem[]>([]);
  const [supLoadingItems, setSupLoadingItems] = useState(false);
  const [supErrorItems,   setSupErrorItems]   = useState<string | null>(null);
  const supItemOptions = useMemo<MultiSelectOption[]>(() => {
    const seen = new Set<string>();
    const out: MultiSelectOption[] = [];
    for (const it of supOrderItems) {
      const code = String(it.code ?? "").trim();
      const desc = String(it.description ?? "").trim();
      const label = code && desc ? `${code} - ${desc}` : (desc || code);
      if (!label) continue;
      const key = label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ value: label, label });
    }
    out.sort((a, b) => a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" }));
    return out;
  }, [supOrderItems]);

  const supOrderIdsKey = useMemo(
    () => supPedidos.map((p) => p.purchaseOrderId).sort().join(","),
    [supPedidos],
  );

  useEffect(() => {
    if (supPedidos.length === 0) { setSupOrderItems([]); setSupLoadingItems(false); return; }

    const dateMap = new Map<string, string>();
    for (const p of supPedidos) { if (p.date) dateMap.set(p.purchaseOrderId, p.date); }

    const batches = chunk(supPedidos.map((p) => p.purchaseOrderId), BATCH_SIZE);
    setSupLoadingItems(true);
    setSupErrorItems(null);

    Promise.all(batches.map((b) => fetchItemsByOrderIds(b)))
      .then((results) => {
        const all = results.flat().map((it) => ({
          ...it,
          orderDate: it.orderDate || dateMap.get(it.orderId) || undefined,
        }));
        setSupOrderItems(all);
      })
      .catch((err: unknown) => {
        setSupErrorItems(err instanceof Error ? err.message : "Erro ao carregar insumos.");
        setSupOrderItems([]);
      })
      .finally(() => setSupLoadingItems(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supOrderIdsKey]);

  /* ═══════════════════════════════════════════════════════════════════════════
   * COMERCIAL
   * ════════════════════════════════════════════════════════════════════════ */

  const [comValues,  setComValues]  = useState<FilterValues>(standardDefaultFilters);
  const [comApplied, setComApplied] = useState<FilterValues>(standardDefaultFilters);
  const [comData,    setComData]    = useState<ComercialData | null>(null);
  const [comLoading, setComLoading] = useState(false);
  const [comError,   setComError]   = useState<string | null>(null);
  const comAbortRef     = useRef<AbortController | null>(null);
  const comHasLoadedRef = useRef(false);
  const [comRefreshTick, setComRefreshTick] = useState(0);

  /**
   * Comercial: só consulta a API após «Aplicar» ou «Atualizar». Não auto-carrega ao abrir a aba.
   */
  const comApply = useCallback(() => {
    comHasLoadedRef.current = true;
    setComRefreshTick((n) => n + 1);
  }, []);

  const comAutoLoad = useCallback(() => {}, []);

  const comReset = useCallback(() => {
    const defaults = standardDefaultFilters();
    setComValues(defaults);
    setComApplied({ ...defaults });
    setComData(null);
    comHasLoadedRef.current = false;
  }, []);

  useEffect(() => {
    if (!comHasLoadedRef.current) return;
    if (allCcIds === undefined) return;

    comAbortRef.current?.abort();
    const ctrl = new AbortController();
    comAbortRef.current = ctrl;

    setComLoading(true);
    setComError(null);

    const payload: FilterValues = {
      ...comValues,
      costCenter: Array.isArray(comValues.costCenter) ? comValues.costCenter : [],
    };

    fetchComercialData(payload, allCcIds, ctrl.signal)
      .then((result) => {
        if (!ctrl.signal.aborted) {
          setComData(result);
          setComLoading(false);
          setComApplied(payload);
        }
      })
      .catch((e) => {
        if (!ctrl.signal.aborted) {
          setComError(e instanceof Error ? e.message : String(e));
          setComLoading(false);
        }
      });

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comRefreshTick, allCcIds]);

  /* ═══════════════════════════════════════════════════════════════════════════
   * ORÇAMENTO
   * ════════════════════════════════════════════════════════════════════════ */

  const orcDates = useMemo(orcDefaultDates, []);

  const [orcBuildingId, setOrcBuildingId] = useState("");
  const [orcStartDate,  setOrcStartDate]  = useState(orcDates.startDate);
  const [orcEndDate,    setOrcEndDate]    = useState(orcDates.endDate);
  const [orcApplied,    setOrcApplied]    = useState<OrcamentoFilters | null>(null);
  const [orcData,       setOrcData]       = useState<OrcamentoData | null>(null);
  const [orcLoading,    setOrcLoading]    = useState(false);
  const [orcError,      setOrcError]      = useState<string | null>(null);
  const orcDidAutoSelect = useRef(false);
  const [orcRefreshTick, setOrcRefreshTick] = useState(0);

  const orcApply = useCallback(() => {
    if (!orcBuildingId) return;
    setOrcApplied({ buildingId: orcBuildingId, startDate: orcStartDate, endDate: orcEndDate });
    setOrcRefreshTick((n) => n + 1);
  }, [orcBuildingId, orcStartDate, orcEndDate]);

  const orcApplyWith = useCallback((f: OrcamentoFilters) => {
    setOrcBuildingId(f.buildingId);
    setOrcStartDate(f.startDate);
    setOrcEndDate(f.endDate);
    setOrcApplied(f);
    setOrcRefreshTick((n) => n + 1);
  }, []);

  const orcReset = useCallback(() => {
    const d = orcDefaultDates();
    const first = ccOptions[0]?.value ?? "";
    setOrcBuildingId(first);
    setOrcStartDate(d.startDate);
    setOrcEndDate(d.endDate);
    setOrcApplied(first ? { buildingId: first, startDate: d.startDate, endDate: d.endDate } : null);
    setOrcRefreshTick((n) => n + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccOptions]);

  useEffect(() => {
    if (!orcApplied) {
      setOrcData(null);
      setOrcLoading(false);
      return;
    }
    const { buildingId, startDate, endDate } = orcApplied;

    if (!buildingId) {
      setOrcData(null);
      setOrcLoading(false);
      return;
    }

    setOrcLoading(true);
    setOrcError(null);

    Promise.all([
      fetchBudgetResources(buildingId),
      fetchAppropriatedResources(buildingId, startDate, endDate),
    ])
      .then(([budgetResources, appropriated]) => {
        setOrcData({ budgetResources, appropriated });
        setOrcLoading(false);
      })
      .catch((err: unknown) => {
        const msg =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          (err instanceof Error ? err.message : "Erro ao carregar dados de orçamento.");
        setOrcError(msg);
        setOrcLoading(false);
      });
  }, [orcApplied, orcRefreshTick]);

  /* ═══════════════════════════════════════════════════════════════════════════
   * GERENCIAL
   * ════════════════════════════════════════════════════════════════════════ */

  const gerDefaults = useMemo(gerencialDefaultDates, []);
  const [gerValues,  setGerValues]  = useState<FilterValues>(gerDefaults);
  const [gerApplied, setGerApplied] = useState<FilterValues>(gerDefaults);
  const [gerData,    setGerData]    = useState<GerencialData | null>(null);
  const [gerLoading, setGerLoading] = useState(false);
  const [gerError,   setGerError]   = useState<string | null>(null);
  const gerAbortRef = useRef<AbortController | null>(null);
  const [gerRefreshTick, setGerRefreshTick] = useState(0);

  /** Só muda ao clicar em Aplicar / Atualizar — evita refetch ao mexer no multiselect de CC. */
  const gerAppliedFetchKey = useMemo(() => {
    const cc = Array.isArray(gerApplied.costCenter) ? [...gerApplied.costCenter].sort().join(",") : "";
    return [gerApplied.startDate, gerApplied.endDate, cc].join("|");
  }, [gerApplied]);

  const gerApply = useCallback(() => {
    if (!gerValues.costCenter || gerValues.costCenter.length === 0) {
      setGerError("Selecione pelo menos um centro de custo.");
      return;
    }
    setGerError(null);
    setGerApplied((prev) => ({ ...prev, ...gerValues }));
    setGerRefreshTick((n) => n + 1);
  }, [gerValues]);

  /** Entrada na aba não força Sienge; Gerencial só carrega após Aplicar. */
  const gerAutoLoad = useCallback(() => {}, []);

  const gerReset = useCallback(() => {
    const d = gerencialDefaultDates();
    setGerValues(d);
    setGerApplied({ ...d });
    setGerData(null);
    setGerError(null);
    setGerLoading(false);
  }, []);

  useEffect(() => {
    if (allCcIds === undefined) return;

    const filters: FilterValues = {
      ...gerApplied,
      costCenter: Array.isArray(gerApplied.costCenter) ? gerApplied.costCenter : [],
    };
    const cc = filters.costCenter;
    if (!cc || cc.length === 0) {
      setGerData(null);
      setGerLoading(false);
      return;
    }

    gerAbortRef.current?.abort();
    const ctrl = new AbortController();
    gerAbortRef.current = ctrl;

    setGerLoading(true);
    setGerError(null);

    fetchGerencialData(filters, allCcIds)
      .then((result) => {
        if (!ctrl.signal.aborted) {
          setGerData(result);
          setGerLoading(false);
        }
      })
      .catch((e) => {
        if (!ctrl.signal.aborted) {
          setGerError(e instanceof Error ? e.message : String(e));
          setGerLoading(false);
        }
      });

    return () => ctrl.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gerAppliedFetchKey, gerRefreshTick, allCcIds]);

  /* ═══════════════════════════════════════════════════════════════════════════
   * CHÃO DE OBRA (lista — overlay / botão Aplicar alinhados ao Financeiro)
   * ════════════════════════════════════════════════════════════════════════ */

  const [chaoListLoading, setChaoListLoading] = useState(false);
  const [chaoUnitTransitionLoading, setChaoUnitTransitionLoading] = useState(false);
  const [chaoListCache, setChaoListCache] = useState<ChaoDeObraListCache>(emptyChaoListCache);

  const persistChaoListCache = useCallback((patch: Partial<ChaoDeObraListCache>) => {
    setChaoListCache((prev) => ({ ...prev, ...patch, hasData: patch.hasData ?? true }));
  }, []);

  const clearChaoListCache = useCallback(() => {
    setChaoListCache(emptyChaoListCache());
  }, []);

  /* ═══════════════════════════════════════════════════════════════════════════
   * Context value
   * ════════════════════════════════════════════════════════════════════════ */

  const value = useMemo<ModulesContextValue>(() => ({
    ccOptions,
    loadingCC,
    ccNameMap,
    financeiro: {
      values:   finValues,
      applied:  finApplied,
      setValues: setFinValues,
      apply:    finApply,
      reset:    finReset,
      autoLoad: finAutoLoad,
      data:     finData,
      loading:  finLoading,
      error:    finError,
    },
    suprimentos: {
      values:   supValues,
      applied:  supApplied,
      setValues: setSupValues,
      apply:    supApply,
      applyWith: supApplyWith,
      reset:    supReset,
      autoLoad: supAutoLoad,
      data:     supData,
      loading:  supLoading,
      error:    supError,
      orderItems:   supOrderItems,
      loadingItems: supLoading || supLoadingItems,
      errorItems:   supErrorItems,
      supplierOptions: supSupplierOptions,
      itemOptions: supItemOptions,
    },
    comercial: {
      values:   comValues,
      applied:  comApplied,
      setValues: setComValues,
      apply:    comApply,
      reset:    comReset,
      autoLoad: comAutoLoad,
      data:     comData,
      loading:  comLoading,
      error:    comError,
    },
    orcamento: {
      buildingId:    orcBuildingId,
      setBuildingId: setOrcBuildingId,
      startDate:     orcStartDate,
      setStartDate:  setOrcStartDate,
      endDate:       orcEndDate,
      setEndDate:    setOrcEndDate,
      applied:       orcApplied,
      apply:         orcApply,
      applyWith:     orcApplyWith,
      reset:         orcReset,
      didAutoSelect: orcDidAutoSelect,
      data:          orcData,
      loading:       orcLoading,
      error:         orcError,
    },
    gerencial: {
      values:   gerValues,
      applied:  gerApplied,
      setValues: setGerValues,
      apply:    gerApply,
      reset:    gerReset,
      autoLoad: gerAutoLoad,
      data:     gerData,
      loading:  gerLoading,
      error:    gerError,
    },
    chaoDeObra: {
      listLoading: chaoListLoading,
      setListLoading: setChaoListLoading,
      unitTransitionLoading: chaoUnitTransitionLoading,
      setUnitTransitionLoading: setChaoUnitTransitionLoading,
      listCache: chaoListCache,
      persistListCache: persistChaoListCache,
      clearListCache: clearChaoListCache,
    },
  }), [
    ccOptions, loadingCC, ccNameMap,
    finValues, finApplied, finApply, finReset, finAutoLoad, finData, finLoading, finError,
    supValues, supApplied, supApply, supApplyWith, supReset, supAutoLoad, supData, supLoading, supError,
    supOrderItems, supLoadingItems, supErrorItems, supSupplierOptions, supItemOptions,
    comValues, comApplied, comApply, comReset, comAutoLoad, comData, comLoading, comError,
    orcBuildingId, orcStartDate, orcEndDate, orcApplied, orcApply, orcApplyWith, orcReset,
    orcData, orcLoading, orcError,
    gerValues, gerApplied, gerApply, gerReset, gerAutoLoad, gerData, gerLoading, gerError,
    chaoListLoading,
    chaoUnitTransitionLoading,
    chaoListCache,
    persistChaoListCache,
    clearChaoListCache,
  ]);

  return (
    <ModulesContext.Provider value={value}>
      {children}
    </ModulesContext.Provider>
  );
}
